import React, { useState } from "react";
import Button from "components/BaseWidgets/button";
import { ClockIcon, CloseIcon } from "assets/icons";
import "./style.scss";
import Divider from "components/BaseWidgets/divider";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchSavedSearches, removeSavedSearch } from "api/search";
import { getKeyword } from "utils/formatHelper";
import { useNavigate } from "react-router-dom";
import Skeleton from "components/BaseWidgets/skeleton";

function RecentSearches({ selectOptions, setOpen }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState({});
  const queryClient = useQueryClient();
  const search_mode = 1;

  const { data, isLoading } = useQuery({
    queryKey: ["savedSearches", { page: 1, limit: 10, search_mode }],
    queryFn: fetchSavedSearches,
    enabled: !!search_mode,
  });

  const recentSearches = data?.data?.saved_searches || [];

  // 🆕 Mutation: remove single or all searches
  const { mutate: handleRemove } = useMutation({
    mutationFn: removeSavedSearch,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["savedSearches", { page: 1, limit: 10, search_mode }],
      });
    },
  });

  const toggleExpand = (index) => {
    setExpanded((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  if (isLoading) return <Skeleton label={false} height={300} />;

  return (
    <div className="recent-searches">
      <div className="flex items-center justify-between gap-3 title-container">
        <h1 className="font-title-normal">Recent searches</h1>
        {recentSearches.length > 0 && (
          <Button
            variant="link"
            onClick={() =>
              handleRemove({ remove_all: true, search_type: 1, search_mode })
            }
          >
            Clear all
          </Button>
        )}
      </div>

      {recentSearches.length === 0 ? (
        <p className="text-sm text-gray-500">No recent searches yet.</p>
      ) : (
        <div className="flex flex-col gap-3 w-full">
          {recentSearches.map((item, i) => {
            const keyword = getKeyword(item, selectOptions);
            const isLong = keyword.length > 55;
            const isExpanded = expanded[i];

            const handleSelect = () => {
              const params = new URLSearchParams();

              // If there's a non-null keyword, use that only
              if (item.keyword) {
                params.set("k", item.keyword);
                params.set("st", "1");
              } else {
                // No keyword — process filter_data normally
                Object.entries(item.filter_data || {}).forEach(
                  ([key, value]) => {
                    if (Array.isArray(value)) {
                      value.forEach((v) => params.append(key, v));
                    } else if (value !== undefined && value !== null) {
                      params.set(key, value);
                    }
                  },
                );
                params.set("st", "2");
              }

              navigate(`/s/${item.id}?${params.toString()}`);
              setOpen(false);
            };

            return (
              <React.Fragment key={item.id}>
                <div className="flex gap-2 items-start ">
                  <span className="clock-icon mt-1">
                    <ClockIcon />
                  </span>
                  <div className="flex-1 pointer">
                    <p
                      onClick={handleSelect}
                      className="font-title-xs break-words"
                    >
                      {isLong && !isExpanded
                        ? `${keyword.slice(0, 55)}... `
                        : keyword}
                    </p>
                    {isLong && (
                      <Button
                        variant="link"
                        onClick={() => toggleExpand(i)}
                        className="more-link"
                      >
                        {isExpanded ? "Less" : "More"}
                      </Button>
                    )}
                  </div>
                  <Button
                    className="close-btn"
                    height={16}
                    variant="custom"
                    onClick={() =>
                      handleRemove({ id: item.id, search_type: 1, search_mode })
                    }
                  >
                    <CloseIcon />
                  </Button>
                </div>
                {i < recentSearches.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default RecentSearches;
